/**
 * Consent Filters Component
 *
 * Filter panel for consent request page with single-select dropdowns
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Filter, Check, RotateCw, Search } from 'lucide-react';
import { Dropdown, DropdownTrigger, DropdownContent, DropdownItem, ChevronIcon, Button } from 'equal-ds-ui';
import type { ConsentFiltersProps } from '../../../types/consent';

// Custom search input that prevents focus loss and blocks Radix typeahead
const DropdownSearchInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus when mounted
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Stop all keyboard events from reaching Radix dropdown (prevents typeahead)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <div
      className="flex items-center gap-1 px-2 py-2 border-b border-border-default group mb-1"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={handleKeyDown}
      onKeyDownCapture={handleKeyDown}
    >
      <Search size={16} strokeWidth={2} className="text-text-tertiary group-hover:text-primary-500 transition-colors duration-200" />
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyDownCapture={handleKeyDown}
        onKeyUp={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className="flex-1 pt-0.5 pl-0.5 text-sm text-text-primary bg-transparent border-0 placeholder:text-text-tertiary placeholder:text-sm placeholder:font-normal placeholder:tracking-wider focus:outline-none focus:ring-0"
      />
    </div>
  );
};

// Memoized dropdown item to prevent unnecessary re-renders
const MemoizedDropdownItem = React.memo(({
  option,
  isSelected,
  onSelect,
  getItemStyles
}: {
  option: { value: string; label: string };
  isSelected: boolean;
  onSelect: () => void;
  getItemStyles: (isSelected: boolean) => React.CSSProperties;
}) => (
  <DropdownItem
    key={option.value}
    onSelect={onSelect}
    style={getItemStyles(isSelected)}
    className={`cursor-pointer transition-colors duration-150 ${
      isSelected
        ? ''
        : 'hover:bg-primary-50'
    }`}
  >
    {option.label}
  </DropdownItem>
));

// Helper to format single-select display
const formatSingleSelectDisplay = (
  selected: string | null,
  options: { value: string; label: string }[],
  noneLabel: string
): string => {
  if (!selected) return noneLabel;
  const option = options.find(opt => opt.value === selected);
  return option?.label || noneLabel;
};

export const ConsentFilters: React.FC<ConsentFiltersProps> = ({
  filterOptions,
  selectedFilters,
  onFiltersChange,
  onApplyFilters,
  onResetFilters,
  hasUnappliedChanges,
  hasAnyFiltersSelected,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Search states for each dropdown
  const [templateSearch, setTemplateSearch] = useState('');
  const [purposeSearch, setPurposeSearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');
  const [aggregatorSearch, setAggregatorSearch] = useState('');

  // Individual hover states for animation
  const [isTemplateHovered, setIsTemplateHovered] = useState(false);
  const [isPurposeHovered, setIsPurposeHovered] = useState(false);
  const [isStatusHovered, setIsStatusHovered] = useState(false);
  const [isAggregatorHovered, setIsAggregatorHovered] = useState(false);

  // Filtered options based on search (only searches the visible label, not the internal value)
  const filteredTemplates = useMemo(() => {
    if (!templateSearch) return filterOptions.templates;
    const search = templateSearch.toLowerCase();
    return filterOptions.templates.filter(opt =>
      opt.label.toLowerCase().includes(search)
    );
  }, [filterOptions.templates, templateSearch]);

  const filteredPurposes = useMemo(() => {
    if (!purposeSearch) return filterOptions.purposeCodes;
    const search = purposeSearch.toLowerCase();
    return filterOptions.purposeCodes.filter(opt =>
      opt.label.toLowerCase().includes(search)
    );
  }, [filterOptions.purposeCodes, purposeSearch]);

  const filteredStatuses = useMemo(() => {
    if (!statusSearch) return filterOptions.statuses;
    const search = statusSearch.toLowerCase();
    return filterOptions.statuses.filter(opt =>
      opt.label.toLowerCase().includes(search)
    );
  }, [filterOptions.statuses, statusSearch]);

  const filteredAggregators = useMemo(() => {
    if (!aggregatorSearch) return filterOptions.aggregators;
    const search = aggregatorSearch.toLowerCase();
    return filterOptions.aggregators.filter(opt =>
      opt.label.toLowerCase().includes(search)
    );
  }, [filterOptions.aggregators, aggregatorSearch]);

  // Create stable keys based on filtered item values (not just length)
  // This ensures dropdown remounts when actual items change, but not on every keystroke
  const statusContentKey = useMemo(
    () => `status-${filteredStatuses.map(s => s.value).join(',')}`,
    [filteredStatuses]
  );
  const purposeContentKey = useMemo(
    () => `purpose-${filteredPurposes.map(p => p.value).join(',')}`,
    [filteredPurposes]
  );
  const templateContentKey = useMemo(
    () => `template-${filteredTemplates.map(t => t.value).join(',')}`,
    [filteredTemplates]
  );
  const aggregatorContentKey = useMemo(
    () => `aggregator-${filteredAggregators.map(a => a.value).join(',')}`,
    [filteredAggregators]
  );

  // Create trigger styles with hover effect
  const createTriggerStyles = (isHovered: boolean) => ({
    transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: isHovered ? 'var(--color-background-primary)' : 'var(--color-background-secondary)',
    color: 'var(--color-text-primary)',
    borderColor: isHovered ? 'var(--color-border-hover)' : 'var(--color-border-default)',
    fontSize: 'var(--typography-fontSize-sm)',
    fontWeight: 'var(--typography-fontWeight-medium)',
    letterSpacing: '0.05em',
    padding: 'var(--spacing-2)',
    borderRadius: 'var(--border-radius-lg)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderBottomWidth: '2px',
    boxShadow: isHovered ? 'var(--shadow-md)' : 'var(--core-shadows-sm)',
  });

  const getFilterLabelStyles = () => ({
    textTransform: 'uppercase' as const,
    fontSize: '11px',
    letterSpacing: '0.1em',
  });

  // Memoized styles to prevent re-renders
  const dropdownContentStyles = useMemo(() => ({
    maxWidth: '300px',
    maxHeight: '250px',
    overflowY: 'auto' as const,
    zIndex: 1000,
  }), []);

  const getItemStyles = useCallback((isSelected: boolean): React.CSSProperties => ({
    // Only set backgroundColor for selected items; non-selected items use CSS hover
    ...(isSelected ? { backgroundColor: 'var(--color-primary-100)' } : {}),
    fontWeight: isSelected ? 600 : 400,
  }), []);

  // Memoized select handlers to prevent re-renders
  const handleTemplateSelect = useCallback((value: string) => {
    onFiltersChange({ selectedTemplate: value });
  }, [onFiltersChange]);

  const handlePurposeSelect = useCallback((value: string) => {
    onFiltersChange({ selectedPurposeCode: value });
  }, [onFiltersChange]);

  const handleStatusSelect = useCallback((value: string) => {
    onFiltersChange({ selectedStatus: value });
  }, [onFiltersChange]);

  const handleAggregatorSelect = useCallback((value: string) => {
    onFiltersChange({ selectedAggregator: value });
  }, [onFiltersChange]);

  // Stable onOpenChange handlers to prevent dropdown re-initialization
  const handleStatusOpenChange = useCallback((open: boolean) => {
    if (!open) setStatusSearch('');
  }, []);

  const handlePurposeOpenChange = useCallback((open: boolean) => {
    if (!open) setPurposeSearch('');
  }, []);

  const handleTemplateOpenChange = useCallback((open: boolean) => {
    if (!open) setTemplateSearch('');
  }, []);

  const handleAggregatorOpenChange = useCallback((open: boolean) => {
    if (!open) setAggregatorSearch('');
  }, []);

  return (
    <div className="bg-white border border-border-default rounded-xl p-4 shadow-md mb-4">
      {/* Filter Header with Expand/Collapse */}
      <div
        className="flex justify-between items-center cursor-pointer rounded-lg p-2 -m-2 transition-colors duration-200 hover:bg-background-primary"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="bg-background-tertiary p-1.5 rounded-lg">
            <Filter className="w-4 h-4 text-text-primary" />
          </div>
          <h2 className="text-xl font-medium text-text-primary tracking-wider">
            Filter By
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <div className="p-1 rounded hover:bg-background-primary hover:border hover:border-border-default transition-colors duration-200">
            <ChevronIcon
              isOpen={isExpanded}
              className="text-text-primary"
              duration={200}
            />
          </div>
        </div>
      </div>

      {/* Filter Row - Animated Expand/Collapse */}
      <div
        className="flex flex-wrap gap-4 w-full overflow-hidden"
        style={{
          transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-out, margin-top 0.3s ease-out',
          maxHeight: isExpanded ? '400px' : '0px',
          opacity: isExpanded ? 1 : 0,
          marginTop: isExpanded ? '16px' : '0px',
          pointerEvents: isExpanded ? 'auto' as const : 'none' as const,
        }}
      >
        {/* Consent Status Filter */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-normal text-text-tertiary tracking-wide mb-1" style={getFilterLabelStyles()}>
            Consent Status
          </label>
          <Dropdown onOpenChange={handleStatusOpenChange}>
            <DropdownTrigger
              variant="default"
              className="w-full"
              style={createTriggerStyles(isStatusHovered)}
              onMouseEnter={() => setIsStatusHovered(true)}
              onMouseLeave={() => setIsStatusHovered(false)}
            >
              <span className={`flex-1 text-left ${!selectedFilters.selectedStatus ? 'text-text-secondary' : ''}`}>
                {formatSingleSelectDisplay(
                  selectedFilters.selectedStatus,
                  filterOptions.statuses,
                  'Select Status'
                )}
              </span>
            </DropdownTrigger>
            <DropdownContent
              key={statusContentKey}
              enableSearch={false}
              style={dropdownContentStyles}
              zIndex={1000}
              enableHoverAnimation={false}
            >
              <DropdownSearchInput
                value={statusSearch}
                onChange={setStatusSearch}
                placeholder="Search statuses"
              />
              {filteredStatuses.length === 0 ? (
                <div className="px-2 py-3 text-sm text-text-tertiary text-center">No options available</div>
              ) : (
                filteredStatuses.map((option) => (
                  <MemoizedDropdownItem
                    key={option.value}
                    option={option}
                    isSelected={selectedFilters.selectedStatus === option.value}
                    onSelect={() => handleStatusSelect(option.value)}
                    getItemStyles={getItemStyles}
                  />
                ))
              )}
            </DropdownContent>
          </Dropdown>
        </div>

        {/* Purpose Code Filter */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-normal text-text-tertiary tracking-wide mb-1" style={getFilterLabelStyles()}>
            Purpose Code
          </label>
          <Dropdown onOpenChange={handlePurposeOpenChange}>
            <DropdownTrigger
              variant="default"
              className="w-full"
              style={createTriggerStyles(isPurposeHovered)}
              onMouseEnter={() => setIsPurposeHovered(true)}
              onMouseLeave={() => setIsPurposeHovered(false)}
            >
              <span className={`flex-1 text-left ${!selectedFilters.selectedPurposeCode ? 'text-text-secondary' : ''}`}>
                {formatSingleSelectDisplay(
                  selectedFilters.selectedPurposeCode,
                  filterOptions.purposeCodes,
                  'Select Purpose Code'
                )}
              </span>
            </DropdownTrigger>
            <DropdownContent
              key={purposeContentKey}
              enableSearch={false}
              style={dropdownContentStyles}
              zIndex={1000}
              enableHoverAnimation={false}
            >
              <DropdownSearchInput
                value={purposeSearch}
                onChange={setPurposeSearch}
                placeholder="Search purpose codes"
              />
              {filteredPurposes.length === 0 ? (
                <div className="px-2 py-3 text-sm text-text-tertiary text-center">No options available</div>
              ) : (
                filteredPurposes.map((option) => (
                  <MemoizedDropdownItem
                    key={option.value}
                    option={option}
                    isSelected={selectedFilters.selectedPurposeCode === option.value}
                    onSelect={() => handlePurposeSelect(option.value)}
                    getItemStyles={getItemStyles}
                  />
                ))
              )}
            </DropdownContent>
          </Dropdown>
        </div>

        {/* Consent Template Filter */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-normal text-text-tertiary tracking-wide mb-1" style={getFilterLabelStyles()}>
            Consent Template
          </label>
          <Dropdown onOpenChange={handleTemplateOpenChange}>
            <DropdownTrigger
              variant="default"
              className="w-full"
              style={createTriggerStyles(isTemplateHovered)}
              onMouseEnter={() => setIsTemplateHovered(true)}
              onMouseLeave={() => setIsTemplateHovered(false)}
            >
              <span className={`flex-1 text-left ${!selectedFilters.selectedTemplate ? 'text-text-secondary' : ''}`}>
                {formatSingleSelectDisplay(
                  selectedFilters.selectedTemplate,
                  filterOptions.templates,
                  'Select Template'
                )}
              </span>
            </DropdownTrigger>
            <DropdownContent
              key={templateContentKey}
              enableSearch={false}
              style={dropdownContentStyles}
              zIndex={1000}
              enableHoverAnimation={false}
            >
              <DropdownSearchInput
                value={templateSearch}
                onChange={setTemplateSearch}
                placeholder="Search templates"
              />
              {filteredTemplates.length === 0 ? (
                <div className="px-2 py-3 text-sm text-text-tertiary text-center">No options available</div>
              ) : (
                filteredTemplates.map((option) => (
                  <MemoizedDropdownItem
                    key={option.value}
                    option={option}
                    isSelected={selectedFilters.selectedTemplate === option.value}
                    onSelect={() => handleTemplateSelect(option.value)}
                    getItemStyles={getItemStyles}
                  />
                ))
              )}
            </DropdownContent>
          </Dropdown>
        </div>

        {/* Account Aggregator Filter */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-normal text-text-tertiary tracking-wide mb-1" style={getFilterLabelStyles()}>
            Account Aggregator
          </label>
          <Dropdown onOpenChange={handleAggregatorOpenChange}>
            <DropdownTrigger
              variant="default"
              className="w-full"
              style={createTriggerStyles(isAggregatorHovered)}
              onMouseEnter={() => setIsAggregatorHovered(true)}
              onMouseLeave={() => setIsAggregatorHovered(false)}
            >
              <span className={`flex-1 text-left ${!selectedFilters.selectedAggregator ? 'text-text-secondary' : ''}`}>
                {formatSingleSelectDisplay(
                  selectedFilters.selectedAggregator,
                  filterOptions.aggregators,
                  'Select Aggregator'
                )}
              </span>
            </DropdownTrigger>
            <DropdownContent
              key={aggregatorContentKey}
              enableSearch={false}
              style={dropdownContentStyles}
              zIndex={1000}
              enableHoverAnimation={false}
            >
              <DropdownSearchInput
                value={aggregatorSearch}
                onChange={setAggregatorSearch}
                placeholder="Search aggregators"
              />
              {filteredAggregators.length === 0 ? (
                <div className="px-2 py-3 text-sm text-text-tertiary text-center">No options available</div>
              ) : (
                filteredAggregators.map((option) => (
                  <MemoizedDropdownItem
                    key={option.value}
                    option={option}
                    isSelected={selectedFilters.selectedAggregator === option.value}
                    onSelect={() => handleAggregatorSelect(option.value)}
                    getItemStyles={getItemStyles}
                  />
                ))
              )}
            </DropdownContent>
          </Dropdown>
        </div>

        {/* Submit/Reset buttons - bottom right */}
        {hasAnyFiltersSelected && (
          <div className="w-full flex justify-end gap-2 mt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                onApplyFilters();
              }}
              disabled={!hasUnappliedChanges}
            >
              <Check className="w-3 h-3" />
              Submit
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                onResetFilters();
              }}
            >
              <RotateCw className="w-3 h-3" />
              Reset
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
